/**
 * DownloadMenuWithFilters
 *
 * Wrapper component that connects DownloadMenu with the AnalyticsTableContext
 * to pass column filters for CSV/XLSX download functionality.
 *
 * This ensures downloaded files contain exactly the same data visible in the filtered table view.
 */

import { FC, useMemo } from 'react';
import { DownloadMenu } from '../../../molecules/dataTable/component/DownloadMenu';
import { useAnalyticsData } from '../context/AnalyticsDataContext';
import { TableTab } from './useTable';
import { ChargeTableFilters, InvoiceTableFilters, ShipmentTableFilters } from './analyticsTableFilterTypes';

interface DownloadMenuWithFiltersProps {
  moreActionEl: HTMLButtonElement | null;
  onCloseMoreAction: () => void;
  tableTab: TableTab;
  customFileName?: string;
  pageSize?: number;
}

/**
 * Wrapper component that passes column filters from AnalyticsTableContext to DownloadMenu
 * This enables downloads to include the same filters that are applied to the table view.
 */
export const DownloadMenuWithFilters: FC<DownloadMenuWithFiltersProps> = ({ moreActionEl, onCloseMoreAction, tableTab, customFileName, pageSize }) => {
  // Get the current filter state from the analytics table context
  const { filters: columnFilters } = useAnalyticsData();

  // Map unified column filters to specific table filters
  // Only 5 columns cross-filter across tables: shipmentNumber, invoiceNumber, serviceProviderCode, modality, billedEntity
  const filterProps = useMemo(() => {
    if (!columnFilters) return {};

    // Cross-filter fields (shared across all tables)
    const crossFilterFields = {
      shipmentNumber: columnFilters.shipmentNumber || [],
      invoiceNumber: columnFilters.invoiceNumber || [],
      serviceProviderCode: columnFilters.serviceProviderCode || [],
      modality: columnFilters.modality || [],
      billedEntity: columnFilters.billedEntity || [],
    };

    // Invoice-specific fields
    const invoiceTableFilters: InvoiceTableFilters = {
      ...crossFilterFields,
      invoiceStatus: columnFilters.invoiceStatus || [],
      fapRef: columnFilters.fapRef || [],
      accountNumber: columnFilters.accountNumber || [],
      invoiceReason: columnFilters.invoiceReason || [],
      chargeCount: columnFilters.chargeCount || [],
      invoiceChargesCount: columnFilters.invoiceChargesCount || [],
    };

    // Shipment-specific fields
    const shipmentTableFilters: ShipmentTableFilters = {
      ...crossFilterFields,
      serviceType: columnFilters.serviceType || [],
      pol: columnFilters.pol || [],
      pod: columnFilters.pod || [],
      mbl: columnFilters.mbl || [],
      vehicleId: columnFilters.vehicleId || [],
      shipperName: columnFilters.shipperName || [],
      shipperCountry: columnFilters.shipperCountry || [],
      consigneeName: columnFilters.consigneeName || [],
      consigneeCountry: columnFilters.consigneeCountry || [],
      goodsName: columnFilters.goodsName || [],
      reviewStage: columnFilters.reviewStage || [],
      shipmentStatus: columnFilters.shipmentStatus || [],
      shipmentStatusReason: columnFilters.shipmentStatusReason || [],
      chargeCount: columnFilters.chargeCount || [],
      shipmentChargesCount: columnFilters.shipmentChargesCount || [],
    };

    // Charge-specific fields
    const chargeTableFilters: ChargeTableFilters = {
      ...crossFilterFields,
      serviceType: columnFilters.serviceType || [],
      pol: columnFilters.pol || [],
      pod: columnFilters.pod || [],
      chargeDomain: columnFilters.chargeDomain || [],
      chargeName: columnFilters.chargeName || [],
      legType: columnFilters.legType || [],
      quantityUnit: columnFilters.quantityUnit || [],
      chargeStatus: columnFilters.chargeStatus || [],
      chargeStatusReason: columnFilters.chargeStatusReason || [],
      chargeCount: columnFilters.chargeCount || [],
    };

    return {
      invoiceTableFilters,
      shipmentTableFilters,
      chargeTableFilters,
      unifiedTableFilters: columnFilters,
    };
  }, [columnFilters]);

  return (
    <DownloadMenu
      moreActionEl={moreActionEl}
      onCloseMoreAction={onCloseMoreAction}
      tableTab={tableTab}
      customFileName={customFileName}
      pageSize={pageSize}
      otherProps={filterProps}
    />
  );
};
